import {
  AutoCoderFrameworkMatch,
  AutoCoderPipelineRecord,
  AutoCoderRecordCodingView,
  AutoCoderRecordWithCodings,
} from '../types/gotypes'

/**
 * Reading a record the way the shared records view (RecordsExplorer) shows it:
 * the RICHEST version of it.
 *
 * Since the detach, a record belongs to a job and is coded by any number of
 * pipelines (`auto_coder_record_codings` is many-to-many), so "the coding" of a
 * record no longer exists. `/records/query` therefore hands back every coding
 * row, and this module turns that list into the blocks the table stacks: one
 * per coding, each labeled with the pipeline that produced it and rendered
 * through THAT pipeline's framework.
 *
 * Only a COMPLETED coding is evidence. Everything else — a coding still moving
 * through the queue, one that failed, one superseded by a re-code — is a state
 * chip: it has no answer to highlight, and pretending otherwise would show an
 * empty framework as if the record had been coded and found nothing.
 */

/** A record row as any door hands it over: with codings, or the legacy shape. */
export type RecordRow = AutoCoderPipelineRecord & {
  codings?: AutoCoderRecordCodingView[]
}

/**
 * The coding states that mean "this answer is still coming". They are the
 * polling signal for the records query: while one is on screen the page keeps
 * asking, and when the last one settles it stops.
 */
export const IN_FLIGHT_CODING_STATES = ['pending', 'queued', 'running'] as const

/** Shown when a coding's pipeline name came back empty. Defensive only: the FK
 * on `auto_coder_record_codings.pipeline_id` makes an orphan coding
 * unreachable, so this label should never be seen. */
export const UNKNOWN_PIPELINE_LABEL = 'Unknown pipeline'

export interface RecordCodingBlock {
  /** Stable and unique within the record — the React key of the block. */
  key: string
  pipelineId: string
  /** The header the block is labeled with; '' for a legacy record, which has
   * no coding to name and therefore grows no header. */
  label: string
  state: string
  error: string
  /** Populated only for a completed coding; the chip states carry none. */
  matches: AutoCoderFrameworkMatch[]
  isCoded: boolean
  isInFlight: boolean
  completedAt?: string
  source: 'coding' | 'legacy'
}

export interface NormalizedRecord {
  id: string
  interviewId: string
  sourceText: string
  /** Empty means an uncoded record: the row is its raw text and nothing else. */
  blocks: RecordCodingBlock[]
  /** True when the row came from `/records/query` WITH at least one coding. */
  hasCodings: boolean
}

const isInFlightState = (state: string): boolean =>
  (IN_FLIGHT_CODING_STATES as readonly string[]).includes(state)

const timeToString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined

/**
 * One record → the blocks that render it.
 *
 * A row with no `codings` field at all is a LEGACY record (the pipeline-scoped
 * readers still serve those): it keeps its single un-headed evidence block, so
 * the callers that predate this plan look exactly as they did.
 */
export const normalizeRecordCodings = (row: RecordRow): NormalizedRecord => {
  const data = row.data
  const base = {
    id: row.id,
    interviewId: data?.interview_id || row.interview_id || row.id,
    sourceText: data?.source_text || '',
  }

  if (!Array.isArray(row.codings)) {
    return {
      ...base,
      hasCodings: false,
      blocks: [{
        key: `${row.id}-legacy`,
        pipelineId: row.auto_coder_pipeline_id || '',
        label: '',
        state: row.state || '',
        error: row.error || '',
        matches: data?.matches || [],
        isCoded: true,
        isInFlight: isInFlightState(row.state || ''),
        source: 'legacy',
      }],
    }
  }

  const blocks = row.codings.map((c, index) => {
    const state = c.state || ''
    const isCoded = state === 'completed'
    return {
      key: `${row.id}-${c.pipeline_id || 'unknown'}-${index}`,
      pipelineId: c.pipeline_id || '',
      label: c.pipeline_name || UNKNOWN_PIPELINE_LABEL,
      state,
      error: c.error || '',
      matches: isCoded ? (c.result || []) : [],
      isCoded,
      isInFlight: isInFlightState(state),
      completedAt: timeToString(c.completed_at),
      source: 'coding' as const,
    }
  })

  return { ...base, hasCodings: blocks.length > 0, blocks }
}

/**
 * Every pipeline that coded anything on this page, once. The explorer fetches
 * one framework set per id — a page of 50 records coded by two pipelines costs
 * two requests, not fifty.
 */
export const distinctCodingPipelineIds = (rows: RecordRow[]): string[] => {
  const seen: string[] = []
  rows.forEach(row => {
    (row.codings || []).forEach(c => {
      if (c.pipeline_id && !seen.includes(c.pipeline_id)) seen.push(c.pipeline_id)
    })
  })
  return seen
}

/** True while any coding on the page is still moving — the polling gate. */
export const recordsHaveInFlightCodings = (rows: RecordRow[]): boolean =>
  rows.some(row => (row.codings || []).some(c => isInFlightState(c.state || '')))

/** Convenience for callers holding the wire type. */
export const normalizeRecordsWithCodings = (
  rows: AutoCoderRecordWithCodings[],
): NormalizedRecord[] => rows.map(normalizeRecordCodings)
